import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';

// Import configuration and utilities
import config from './solar-health-config.json';
import { useSolarHealthData } from './useSolarHealthData';
import { 
  createChartOption, 
  getLegendItems, 
  validateConfig,
  generateMockHealthData
} from './solar-health-utils';

// Import component styles
import './solar-health-diagnostics.css';

/**
 * Individual Solar Cluster Chart Component
 */
const SolarClusterChart = ({ 
  clusterName, 
  data, 
  lastUpdated, 
  config: chartConfig, 
  className = "",
  onInfoClick = null 
}) => {
  const legendItems = useMemo(() => getLegendItems(chartConfig), [chartConfig]);
  
  const chartOption = useMemo(() => {
    return createChartOption(data, clusterName, chartConfig);
  }, [data, clusterName, chartConfig]);

  const handleInfoClick = () => {
    if (onInfoClick) {
      onInfoClick(clusterName, data);
    }
  };

  return (
    <div className={`solar-cluster-container ${className}`}>
      {/* Header */}
      <div className="solar-cluster-header">
        <div className="solar-cluster-title">
          {clusterName}
          <span 
            className="solar-info-icon"
            onClick={handleInfoClick}
            title={chartConfig.accessibility?.infoIconLabel || `${clusterName} Info`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleInfoClick()}
            aria-label={`Information about ${clusterName}`}
          >
            &#9432;
          </span>
        </div>
        <div 
          className="solar-last-updated"
          title={chartConfig.accessibility?.lastUpdatedLabel || "Last data update time"}
        >
         
        </div>
      </div>

      {/* Legend */}
      <div className="solar-legend">
        {legendItems.map(({ color, label, value }) => (
          <span key={value} className="solar-legend-item">
            <div 
              className="solar-legend-color-box" 
              style={{ backgroundColor: color }} 
            />
            {label}
          </span>
        ))}
      </div>

      {/* Chart */}
      <div className="solar-chart-wrapper">
        <ReactECharts
          option={chartOption}
          style={{ 
            height: chartConfig.chart.dimensions.height, 
            width: '100%' 
          }}
          opts={{ renderer: 'canvas' }}
        />
      </div>
    </div>
  );
};

/**
 * Loading Component
 */
const LoadingState = () => (
  <div className="solar-loading">
    <div className="solar-loading-spinner">⟳</div>
    Loading solar health data...
  </div>
);

/**
 * Error Component
 */
const ErrorState = ({ error, onRetry }) => (
  <div className="solar-error">
    <div className="solar-error-message">
      {error?.message || 'Failed to load solar health data'}
    </div>
    {error?.canRetry && (
      <button className="solar-retry-button" onClick={onRetry}>
        Retry
      </button>
    )}
  </div>
);

/**
 * Main Solar Power Health Diagnostics Component
 * Features:
 * - Modular configuration via JSON
 * - Mock data support for development/testing
 * - Custom hooks for data management
 * - Performance optimizations with React.memo
 * - Proper error handling and retry mechanisms
 * - Accessibility support
 * - Responsive design with CSS
 */
function SolarPowerHealthDiagnostics({ 
  isActive = true,
  customConfig = null,
  onClusterInfoClick = null,
  onDataUpdate = null,
  className = "",
  style = {}
}) {
  // Use custom config if provided, otherwise use default
  const activeConfig = customConfig || config;

  // Validate configuration
  const isConfigValid = useMemo(() => {
    return validateConfig(activeConfig);
  }, [activeConfig]);

  // Custom hook for data management
  const { 
    data, 
    isLoading, 
    error, 
    lastUpdated, 
    retry 
  } = useSolarHealthData(activeConfig, isActive && isConfigValid);

  // Notify parent component when data updates
  React.useEffect(() => {
    if (onDataUpdate && data && Object.keys(data).length > 0) {
      onDataUpdate(data);
    }
  }, [data, onDataUpdate]);

  // Don't render if config is invalid
  if (!isConfigValid) {
    return (
      <div className={`solar-health-container ${className}`} style={style}>
        <div className="solar-cluster-container">
          <ErrorState 
            error={{ message: 'Invalid configuration', canRetry: false }} 
          />
        </div>
      </div>
    );
  }

  // Render loading state
  if (isLoading) {
    return (
      <div className={`solar-health-container ${className}`} style={style}>
        <div className="solar-cluster-container">
          <LoadingState />
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={`solar-health-container ${className}`} style={style}>
        <div className="solar-cluster-container">
          <ErrorState error={error} onRetry={retry} />
        </div>
      </div>
    );
  }

  // Render clusters
  const clusters = Object.entries(data);
  
  if (clusters.length === 0) {
    return (
      <div className={`solar-health-container ${className}`} style={style}>
        <div className="solar-cluster-container">
          <div className="solar-error">
            <div className="solar-error-message">
              No cluster data available
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`solar-health-container ${className}`} 
      style={style}
      role="application"
      aria-label={activeConfig.accessibility?.chartDescription || "Solar panel health status dashboard"}
    >
      {clusters.map(([clusterId, clusterData]) => (
        <SolarClusterChart
          key={clusterId}
          clusterName={clusterData.name}
          data={clusterData.data}
          lastUpdated={lastUpdated}
          config={activeConfig}
          onInfoClick={onClusterInfoClick}
        />
      ))}
    </div>
  );
}

// PropTypes for development
SolarPowerHealthDiagnostics.propTypes = {
  isActive: PropTypes.bool,
  customConfig: PropTypes.object,
  onClusterInfoClick: PropTypes.func,
  onDataUpdate: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object
};

SolarClusterChart.propTypes = {
  clusterName: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.array).isRequired,
  lastUpdated: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  className: PropTypes.string,
  onInfoClick: PropTypes.func
};

ErrorState.propTypes = {
  error: PropTypes.object,
  onRetry: PropTypes.func
};

// Default props
SolarPowerHealthDiagnostics.defaultProps = {
  isActive: true,
  customConfig: null,
  onClusterInfoClick: null,
  onDataUpdate: null,
  className: "",
  style: {}
};

// Memoize component for performance
export default React.memo(SolarPowerHealthDiagnostics);